import { create } from "zustand";
import supabase from "../utils/supabase";

export interface Notification {
  id: string;
  type: "message" | "notification";
  user_id: string;
  read: boolean;
  content: string;
  link?: string;
  sender?: string;
  created_at: string;
}

interface NotificationsStore {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  isDropdownOpen: boolean;

  // Actions
  fetchNotifications: () => Promise<void>;
  fetchNotificationsForDropdown: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  addNotification: (notification: Notification) => void;
  updateNotification: (notification: Notification) => void;
  refreshNotifications: () => Promise<void>;
  setDropdownOpen: (open: boolean) => void;
  subscribeToNotifications: (userId: string) => void;
  unsubscribeFromNotifications: () => void;
}

const useNotificationsStore = create<NotificationsStore>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  isDropdownOpen: false,

  fetchNotifications: async () => {
    set({ isLoading: true, error: null });

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", session.user.id)
        // Run E2 J1: student-teacher chat removed (K-5). Hide old chat ("message") notifications.
        .neq("type", "message")
        .order("created_at", { ascending: false });

      if (error) {
        set({ error: error.message, isLoading: false });
        return;
      }

      const notifications = data || [];
      const unreadCount = notifications.filter((n) => !n.read).length;

      set({
        notifications,
        unreadCount,
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch notifications",
        isLoading: false,
      });
    }
  },

  fetchNotificationsForDropdown: async () => {
    set({ isLoading: true, error: null });

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        set({ isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", session.user.id)
        // Run E2 J1: student-teacher chat removed (K-5). Hide old chat ("message") notifications.
        .neq("type", "message")
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) {
        set({ error: error.message, isLoading: false });
        return;
      }

      const notifications = data || [];
      const unreadCount = notifications.filter((n) => !n.read).length;

      set({
        notifications,
        unreadCount,
        isLoading: false,
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch notifications",
        isLoading: false,
      });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) {
        return;
      }

      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", session.user.id)
        // Run E2 J1: chat ("message") notifications do not count.
        .neq("type", "message")
        .eq("read", false);

      if (error) {
        set({ error: error.message });
        return;
      }

      const unreadCount = count || 0;

      set((state) => ({
        ...state,
        unreadCount: unreadCount,
      }));
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to fetch unread count",
      });
    }
  },

  markAsRead: async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("id", notificationId);

      if (error) {
        set({ error: error.message });
        return;
      }

      // Update local state
      set((state) => {
        const updatedNotifications = state.notifications.map((n) =>
          n.id === notificationId ? { ...n, read: true } : n
        );
        const unreadCount = updatedNotifications.filter((n) => !n.read).length;

        return {
          notifications: updatedNotifications,
          unreadCount,
        };
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to mark notification as read",
      });
    }
  },

  markAllAsRead: async () => {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.user) return;

      const { error } = await supabase
        .from("notifications")
        .update({ read: true })
        .eq("user_id", session.user.id)
        .eq("read", false);

      if (error) {
        set({ error: error.message });
        return;
      }

      // Update local state
      set((state) => {
        const updatedNotifications = state.notifications.map((n) => ({
          ...n,
          read: true,
        }));
        return {
          notifications: updatedNotifications,
          unreadCount: 0,
        };
      });
    } catch (error) {
      set({
        error:
          error instanceof Error
            ? error.message
            : "Failed to mark all notifications as read",
      });
    }
  },

  addNotification: (notification: Notification) => {
    set((state) => {
      // Check if notification already exists (for chat notifications that get updated)
      const existingIndex = state.notifications.findIndex(
        (n) => n.id === notification.id
      );

      let updatedNotifications;
      if (existingIndex !== -1) {
        // Update existing notification
        updatedNotifications = [...state.notifications];
        updatedNotifications[existingIndex] = notification;
      } else {
        // Add new notification at the beginning
        updatedNotifications = [notification, ...state.notifications];
      }

      // Instead of calculating unread count from local array (which might be incomplete),
      // fetch the actual unread count from the database
      get().fetchUnreadCount();

      return {
        notifications: updatedNotifications,
      };
    });
  },

  updateNotification: (notification: Notification) => {
    set((state) => {
      // Find the existing notification
      const existingIndex = state.notifications.findIndex(
        (n) => n.id === notification.id
      );

      let updatedNotifications;
      if (existingIndex !== -1) {
        // Remove the old notification and add the updated one at the top
        updatedNotifications = [
          notification,
          ...state.notifications.filter((n) => n.id !== notification.id),
        ];
      } else {
        // If not found, add it at the top
        updatedNotifications = [notification, ...state.notifications];
      }

      // Instead of calculating unread count from local array (which might be incomplete),
      // fetch the actual unread count from the database
      get().fetchUnreadCount();

      return {
        notifications: updatedNotifications,
      };
    });
  },

  refreshNotifications: async () => {
    await get().fetchNotifications();
  },

  setDropdownOpen: (open: boolean) => {
    set({ isDropdownOpen: open });
  },

  subscribeToNotifications: (userId: string) => {
    const channel = supabase
      .channel("notifications-" + userId)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const newNotification = payload.new as Notification;
          // Run E2 J1: student-teacher chat removed (K-5).
          if (newNotification.type === "message") return;
          get().addNotification(newNotification);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "notifications",
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          const updatedNotification = payload.new as Notification;
          // Run E2 J1: student-teacher chat removed (K-5).
          if (updatedNotification.type === "message") return;
          get().updateNotification(updatedNotification);
        }
      )
      .subscribe();

    // Store the channel reference for cleanup
    (get as any).notificationChannel = channel;
  },

  unsubscribeFromNotifications: () => {
    const channel = (get as any).notificationChannel;
    if (channel) {
      supabase.removeChannel(channel);
      (get as any).notificationChannel = null;
    }
  },
}));

export default useNotificationsStore;
